import { cloneDeep } from 'lodash';

/**
 * Checks if the provided key is a string.
 *
 * @param {any} key - The key to check.
 * @throws {Error} If the key is not a string.
 */
const checkKeyType = (key) => {
    if (typeof key !== 'string') {
        throw new Error('Key must be a string');
    }
};

/**
 * Retrieves a value from storage (localStorage or sessionStorage) by key.
 *
 * @param {string} key - The key of the item to retrieve.
 * @param {Storage} storage - The storage to retrieve from.
 * @returns {string|null} The value of the item in storage, or null if the item does not exist or storage is not available.
 */
const getFromStorage = (key, storage) => {
    checkKeyType(key);

    try {
        return storage.getItem(key);
    } catch (error) {
        console.error(`Failed to retrieve item with key "${key}" from storage`, error);
        return null;
    }
};

/**
 * Removes an item from storage (localStorage or sessionStorage).
 *
 * @param {string} key - The key of the item to remove.
 * @param {Storage} storage - The storage to remove from.
 */
const removeFromStorage = (key, storage) => {
    checkKeyType(key);

    try {
        storage.removeItem(key);
    } catch (error) {
        console.error(`Failed to remove item with key "${key}" from storage`, error);
    }
};

/**
 * Retrieves a value from storage (localStorage or sessionStorage) by key and parses it as JSON.
 *
 * @param {string} key - The key of the item to retrieve.
 * @param {Storage} storage - The storage to retrieve from.
 * @returns {Object|null} The parsed object from storage, or null if the item does not exist, cannot be parsed, or storage is not available.
 */
const getParsedValueFromStorage = (key, storage) => {
    checkKeyType(key);

    try {
        const val = getFromStorage(key, storage);
        return JSON.parse(val);
    } catch (error) {
        console.error(`Failed to parse JSON from item with key "${key}" in storage`, error);
        return null;
    }
};

/**
 * Sets a value in storage (localStorage or sessionStorage).
 *
 * @param {string} key - The key of the item to set.
 * @param {any} val - The value to set. Will be stringified to JSON.
 * @param {Storage} storage - The storage to set in.
 */
const setValueToStorage = (key, val, storage) => {
    checkKeyType(key);

    try {
        storage.setItem(key, JSON.stringify(val));
    } catch (error) {
        console.error(`Failed to set item with key "${key}" in storage`, error);
    }
};

const storageFunctions = (storage) => ({
    get: (key) => getFromStorage(key, storage),
    remove: (key) => removeFromStorage(key, storage),
    getParsed: (key) => getParsedValueFromStorage(key, storage),
    set: (key, val) => setValueToStorage(key, val, storage),
});

export const storageService = {
    local: storageFunctions(window.localStorage),
    session: storageFunctions(window.sessionStorage),
};

/**
 * Determines the appropriate storage type based on the feature flag.
 * If memorization is enabled, uses 'local' storage; otherwise, uses 'session'.
 * Note: This is a temporary solution for essential delivery. The ideal approach will be implemented later, using the 'defaultCustomer' API's extended response.
 *
 * @returns {string} The storage type ('local' or 'session')
 */
const getFilterStorageType = () => {
    const isMemorizationEnabled = window.octopusFeatures?.isLocalStorageFiltersEnabled ?? false;
    return isMemorizationEnabled ? 'local' : 'session';
};

/**
 * Saves filter data in local storage for a specific page.
 *
 * This function retrieves the existing filter data from local storage under the key 'FILTER_KEYS'.
 * If data already exists, it updates the relevant filter data for the given page with the new data.
 * If no data exists, it initializes the filter data for the given page with the provided data.
 * The updated or newly created filter data is then saved back into local storage.
 *
 * @param {string} page - The identifier for the current page.
 * @param {object} data - The filter data to be saved.
 */
export const saveFilterData = (page, data = {}) => {
    const storageType = getFilterStorageType();
    const savedFilterData = storageService[storageType]?.getParsed('FILTER_KEYS');

    if (savedFilterData) {
        const updatedFilterData = {
            ...savedFilterData,
            [page]: {
                ...data,
            },
        };
        storageService[storageType]?.set('FILTER_KEYS', updatedFilterData);
    } else {
        storageService[storageType]?.set('FILTER_KEYS', {
            [page]: {
                ...data,
            },
        });
    }
};

/**
 * Retrieves filter data from local storage based on the provided page.
 *
 * This function fetches previously saved filter settings for a specific page.
 * from local storage using a predefined storage key (`FILTER_KEYS`).
 * It includes validation checks to ensure that the required data exists.
 *
 * @param {string} page - The page identifier for which the filter data is retrieved.
 * @returns {Object|null} - The filter data corresponding to the specified page or `null` if not found or invalid.
 */
export const getFilterData = (page) => {
    const storageType = getFilterStorageType();
    const savedFilterData = storageService[storageType]?.getParsed('FILTER_KEYS');

    return savedFilterData?.[page] || null;
};

/**
 * This function handles the saving and formatting of filter data based on the provided input.
 * It checks if the input data contains a `sessionKey` and `updateFilterData`, then filters
 * After saving, it retrieves the stored filter data for the session and merges it back into
 * the original data object, returning the updated object. If no session data is found,
 * it returns the original data.
 *
 * @param {Object} data - The input data containing filter information and session key.
 * @returns {Object} - The updated data object, potentially merged with stored filters.
 */
export const saveAndFormatFilterData = (data) => {
    // identifying search term is available or not in query
    const isSearchTermAvailable =
        (data?.query && !data?.query.includes('=') && !data?.query.includes('.')) ||
        /^[^.&=]+&/.test(data?.query);
    const formattedQuery = formatQueryString(data?.query);
    if (data?.sessionKey && data?.updateFilterData) {
        let paramsToInclude = ['query'];

        // Include external filters if they exist as an array of keys
        if (Array.isArray(data.externalFilters)) {
            paramsToInclude = [...paramsToInclude, ...data.externalFilters];
        }

        const dataCopy = cloneDeep(data);

        // Only save properties that are part of paramsToInclude and have a truthy value
        const filterData = Object.fromEntries(
            Object.entries(dataCopy).filter(
                ([key, value]) =>
                    paramsToInclude
                        .map((param) => param.toLowerCase())
                        .includes(key.toLowerCase()) && value
            )
        );

        if (!!formattedQuery || isSearchTermAvailable) {
            filterData.query = formattedQuery;
        }

        saveFilterData(dataCopy?.sessionKey, filterData);
    }

    const filters = getFilterData(data?.sessionKey) || {};
    if (isSearchTermAvailable) {
        delete filters?.query;
    }

    if (data?.sessionKey && filters) {
        Object.assign(data, filters);
    }
    // Remove sessionKey, updateFilterData, and externalFilters before returning
    delete data?.sessionKey;
    delete data?.updateFilterData;
    delete data?.externalFilters;

    return data;
};

/**
 * Formats a query string by removing the search Term
 * Example : PO PENDING&searchorderclass.order_division=04
 * In Above query string PO PENDING is the Search term this function is responsible for to remove that.
 * It returns query string searchorderclass.order_division=04
 * @param {string} query - The input query string to format.
 * @returns {string} - The formatted query string with search Term removed if applicable.
 */
function formatQueryString(query) {
    if (!/[.=&]/.test(query)) {
        return '';
    }
    const regex = /^[^.=]+&/;
    if (regex.test(query)) {
        return query.replace(regex, '');
    }
    return query;
}
